'use client';

import { type KeyboardEvent, useCallback, useEffect, useRef } from 'react';

/** A queued Enter that never finds a result is forgotten after this long. */
export const QUEUED_ENTER_TIMEOUT_MS = 4000;

/**
 * Enter with no selected row: the typed term's results have not arrived yet
 * (cmdk would drop the key). Live search reports "not pending" for a render
 * between the debounce settling and the query starting, so this does not
 * depend on the pending flag.
 */
export function shouldQueueEnter(input: {
  key: string;
  hasSelection: boolean;
}): boolean {
  return input.key === 'Enter' && !input.hasSelection;
}

export type QueuedEnterStep = 'idle' | 'wait' | 'open-first' | 'drop';

/** What a queued Enter does on this render. */
export function resolveQueuedEnter(input: {
  queuedTerm: string | null;
  term: string;
  isPending: boolean;
  resultCount: number;
}): QueuedEnterStep {
  if (input.queuedTerm === null) return 'idle';
  if (input.queuedTerm !== input.term) return 'drop';
  return !input.isPending && input.resultCount > 0 ? 'open-first' : 'wait';
}

/**
 * Live search answers a moment after each keystroke. An Enter pressed in
 * that gap has no selected row, so cmdk drops it; this keeps it and opens
 * the first result when the term's results arrive.
 */
export function useQueuedEnter({
  term,
  isPending,
  resultCount,
}: {
  term: string;
  isPending: boolean;
  resultCount: number;
}): (event: KeyboardEvent<HTMLInputElement>) => void {
  // A ref, not state: the results arriving re-render this hook anyway.
  const queuedRef = useRef<{
    term: string;
    at: number;
    root: Element | null;
  } | null>(null);

  useEffect(() => {
    const queued = queuedRef.current;
    const step = resolveQueuedEnter({
      queuedTerm: queued?.term ?? null,
      term,
      isPending,
      resultCount,
    });
    if (!queued || step === 'idle') return;
    const expired = Date.now() - queued.at > QUEUED_ENTER_TIMEOUT_MS;
    if (step === 'wait' && !expired) return;
    queuedRef.current = null;
    if (expired) return;
    if (step === 'open-first') openFirstResult(queued.root);
  }, [isPending, resultCount, term]);

  return useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      const root = event.currentTarget.closest('[cmdk-root]');
      const hasSelection = Boolean(
        root?.querySelector('[cmdk-item][aria-selected="true"]')
      );
      if (
        event.nativeEvent.isComposing ||
        !term.trim() ||
        !shouldQueueEnter({ key: event.key, hasSelection })
      ) {
        return;
      }
      // Rows already shown but none selected: take the first one now.
      if (!isPending && resultCount > 0) {
        openFirstResult(root);
        return;
      }
      queuedRef.current = { term, at: Date.now(), root };
    },
    [isPending, resultCount, term]
  );
}

/** cmdk items select on click; the first row is the one Enter would take. */
function openFirstResult(root: Element | null): void {
  root
    ?.querySelector<HTMLElement>('[cmdk-item]:not([data-disabled="true"])')
    ?.click();
}
